import React, { createContext, useContext, useRef, useEffect } from "react";
import { Toast, ToastMessage } from "primereact/toast";

interface ToastContextType {
    showError: (summary: string, detail?: string) => void;
    showSuccess: (summary: string, detail?: string) => void;
    showInfo: (summary: string, detail?: string) => void;
    showWarn: (summary: string, detail?: string) => void;
    show: (message: ToastMessage) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

// Global toast reference for use outside React components
let globalToastRef: Toast | null = null;

export function showGlobalError(summary: string, detail?: string) {
    globalToastRef?.show({
        severity: "error",
        summary,
        detail,
        life: 5000,
    });
}

export function showGlobalSuccess(summary: string, detail?: string) {
    globalToastRef?.show({
        severity: "success",
        summary,
        detail,
        life: 3000,
    });
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
}

interface ToastProviderProps {
    children: React.ReactNode;
}

export default function ToastProvider({ children }: ToastProviderProps) {
    const toastRef = useRef<Toast>(null);

    useEffect(() => {
        globalToastRef = toastRef.current;
        return () => {
            globalToastRef = null;
        };
    }, []);

    const showError = (summary: string, detail?: string) => {
        toastRef.current?.show({
            severity: "error",
            summary,
            detail,
            life: 5000,
        });
    };

    const showSuccess = (summary: string, detail?: string) => {
        toastRef.current?.show({
            severity: "success",
            summary,
            detail,
            life: 3000,
        });
    };

    const showInfo = (summary: string, detail?: string) => {
        toastRef.current?.show({
            severity: "info",
            summary,
            detail,
            life: 3000,
        });
    };

    const showWarn = (summary: string, detail?: string) => {
        toastRef.current?.show({
            severity: "warn",
            summary,
            detail,
            life: 4000,
        });
    };

    const show = (message: ToastMessage) => {
        toastRef.current?.show(message);
    };

    return (
        <ToastContext.Provider value={{ showError, showSuccess, showInfo, showWarn, show }}>
            <Toast ref={toastRef} position="top-right" />
            {children}
        </ToastContext.Provider>
    );
}
